/**
 * EventCreatePage — the event wizard: BASICS → PLACE → TICKETING → EXTRAS → REVIEW.
 * docs/plans/2026-09-host-event-edit/handoff/README.md (Sept 2026 redesign).
 *
 * One component, three seats:
 *   standalone  /host/events/create (create mode: no tabs, no rail, an 880px column, steps
 *               3–5 locked until Basics and Place are valid, CONTINUE / SKIP / SKIP TO
 *               PUBLISH, then SAVE DRAFT + PUBLISH) and /host/events/:id/edit (the same
 *               wizard in edit mode without the dashboard's tab chrome);
 *   inline      the host dashboard's EDIT tab (every step unlocked, SAVE CHANGES; the
 *               parent owns the step and answers a save with its toast);
 *   embedded    the gathering wizard: one form, no stepper, the money and extras folded.
 *
 * The form state, validation and the payload are the ones the builder has always sent —
 * field parity is a hard requirement of the handoff. Only the presentation is the kit.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  RichTextEditor,
  LoadingSpinner,
  AddressAutocomplete,
  VenuePicker,
  PromoCodesSection,
  TicketTiersSection,
  EventMediaManager,
  LinksEditor,
  CoHostsPanel,
} from '../components';

import PartyQuestionsSection from '../components/PartyQuestionsSection';
import { eventService, clubService, regionService, gatheringService } from '../services';
import { useAuth, useDialog, useUser } from '../context';
import useAutoSave from '../hooks/useAutoSave';
import useTopBarHeight from '../hooks/useTopBarHeight';
import { parseCategories } from '../constants/eventCategories';
import { validateEventForm, basicsErrors, placeErrors, missingLabels, FIELD_ORDER, FIELD_LABELS, FIELD_STEP, SIMPLE_FIELD_STEP } from '../utils/eventValidation';
import { tagTone } from '../components/messages/plan/planModel';
import eventPath from '../utils/eventPath';
import CategoryMultiSelect from '../components/CategoryMultiSelect';
import LinkPagesPanel from '../components/LinkPagesPanel';
import PosterSlot from '../components/PosterSlot';
import { SurfacePage } from '../components/surface';
import HostEventHeader from '../components/host/HostEventHeader';
import CoverReview from '../components/host/CoverReview';
import {
  Card, SectionHead, Field, TextInput, AffixInput, ChoiceCard, ToggleRow, SegPills, SubPanel, RemoveX,
  Btn, Note, Help, Toast, useToast, usd, fmtWhen,
} from '../components/host/kit';

// Validation field order (top-to-bottom) and human-readable labels used to build the
// VALIDATION REQUIRED message.
const STEP_LABELS = ['BASICS', 'PLACE', 'TICKETING', 'EXTRAS', 'REVIEW'];
// The Simple view (create mode only): two steps, the fields a Guaranteed event needs to publish.
// Handoff: docs "Event creator — Simple / Advanced views". Same form state, same validator.
const SIMPLE_STEP_LABELS = ['THE EVENT', 'TICKETS'];
const ADVANCED_ROWS = [
  ['PARTY STARTER', 'The night only happens if the fund goal is hit.'],
  ['CURATED GUESTLIST', 'Approve or decline each guest yourself.'],
  ['PRIVATE EVENT', 'Invite link only, off the Featured Wall.'],
  ['EXTERNAL TICKETS', 'Link guests out to another ticket site.'],
  ['PRICING', 'Pay what you want, ticket tiers, promo codes.'],
  ['FLAKE FEE', 'Charge guests who withdraw from a Party Starter.'],
  ['VENUE & MEDIA', 'A named venue, photo gallery and video.'],
  ['CATEGORIES', 'Up to three, from the full list.'],
  ['EXTRAS', 'House rules, event questions, links, Give/Take board.'],
  ['CO-HOSTS', 'Revenue splits and edit access.'],
  ['LISTINGS', 'Put it on your clubs and Pro pages.'],
];
/** The creator's own path. The view is its last segment: nothing = SIMPLE, `advanced` = the wizard. */
export const EVENT_CREATE_PATH = '/host/events/create';

/** The Advanced-only settings a draft carries, as the Simple view's FROM ADVANCED note lists them. */
export function carriedSettings(formData, loadedEvent) {
  return [
    formData.fundingType === 'party_starter' && 'Party Starter',
    formData.mode === 'curated' && 'Curated guestlist',
    !!formData.isPrivate && 'Private',
    !!formData.isExternalTicketing && 'External tickets',
    formData.pricingMode === 'pay_what_you_want' && 'Pay what you want',
    (loadedEvent?.tiers?.length > 0) && 'Ticket tiers',
    (loadedEvent?.promoCodes?.length > 0) && 'Promo codes',
  ].filter(Boolean);
}
const POSTER_MAX = 10 * 1024 * 1024;

/** The poster drop-zone: DROP AN IMAGE OR BROWSE · Landscape · up to 10 MB. */
function PosterDrop({ onFile, onError, disabled }) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const take = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { onError?.('Please choose an image'); return; }
    if (file.size > POSTER_MAX) { onError?.('Poster must be under 10 MB'); return; }
    onFile?.(file);
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" className="hidden" style={{ display: 'none' }} onChange={(e) => { take(e.target.files?.[0]); e.target.value = ''; }} disabled={disabled} data-testid="poster-file" />
      <button
        type="button"
        className="hev-drop"
        data-active={active ? '' : undefined}
        disabled={disabled}
        onClick={() => ref.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setActive(true); }}
        onDragLeave={() => setActive(false)}
        onDrop={(e) => { e.preventDefault(); setActive(false); take(e.dataTransfer?.files?.[0]); }}
        aria-label="Upload a poster"
      >
        <b>+</b>
        <span>DROP AN IMAGE OR BROWSE</span>
        <small>Landscape · up to 10 MB</small>
      </button>
    </>
  );
}

export default function EventCreatePage({
  embedded = false,
  clubIdProp = null,
  gatheringId = null,
  onComplete = null,
  onCancel = null,
  // `inline` — the builder rendered inside the host dashboard's EDIT tab. The parent
  // passes the event id, drives the wizard step through `step` / `onStepChange`, and is
  // told about a save through `onSaved` (a failed validation through `onInvalid`) instead
  // of a navigation. `saveRef.current()` submits from outside.
  inline = false,
  eventId: eventIdProp = null,
  step: stepProp = null,
  onStepChange = null,
  onSaved = null,
  onInvalid = null,
  onViewPublic = null,
  saveRef = null,
  paidCountProp = null,
  afterSteps = null,
  // Plan detection (Oct 2026): the review sheet renders every section stacked (`sheet`), phone-dense
  // (`compact`), with the chat's values and their tags filled in (`prefill`, see planModel.eventPrefill).
  sheet = false,
  compact = false,
  prefill = null,
  // Create mode opens on SIMPLE unless told otherwise (/host/events/create/advanced, a Party
  // Starter deep link, or a reopened draft that already carries Advanced-only settings).
  defaultView = 'simple',
} = {}) {
  const navigate = useNavigate();
  // When embedded (e.g. in the Gathering wizard) the route's :id is the CLUB id,
  // not an event id — ignore it so the builder stays in create mode.
  const { id: routeId, view: viewFromPath } = useParams();
  const eventId = inline || embedded ? (eventIdProp || undefined) : routeId;
  const [searchParams] = useSearchParams();
  // In embedded mode (e.g. the Gathering wizard) the club is passed as a prop;
  // standalone, it comes from the ?clubId= query param.
  const clubIdFromQuery = clubIdProp ?? searchParams.get('clubId');
  const { isAuthenticated } = useAuth();
  // PLAN IT FOR ME is only dealt when the planner is theirs (`planner_audience`).
  const { canUsePlanner } = useUser();
  const { showConfirm, showAlert } = useDialog();
  const topH = useTopBarHeight();

  const isEditMode = Boolean(eventId);
  // "Float a Party Starter" (the + → Start Something) deep-links here with
  // ?funding=party_starter so the wizard opens straight into the crowdfunded flow.
  const fundingFromQuery = searchParams.get('funding');
  const startAsPartyStarter = !isEditMode && fundingFromQuery === 'party_starter';

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    fundingGoal: '',
    ticketPrice: '',
    suggestedPrice: '',
    pricingMode: 'fixed', // 'fixed' | 'pay_what_you_want' — when PWYW, ticketPrice acts as the floor
    description: '',
    dateTime: '',
    endDateTime: '',
    fundingDeadline: '',
    location: '',
    locationLat: null,
    locationLng: null,
    locationCity: '',
    venueName: '',
    venueAddress: '',
    venueLat: null,
    venueLng: null,
    mode: 'open',
    fundingType: startAsPartyStarter ? 'party_starter' : 'guaranteed',
    rsvpGoal: '', // Phase 10: headcount goal for a free RSVP Party Starter
    category: '',
    categories: [], // no default — the host must pick at least one
    customCategory: '',
    isPrivate: false,
    giveTakeEnabled: false,
    // External ticketing: when true, this is a billboard event — tickets sold
    // off-platform via externalTicketUrl, no internal checkout/pricing.
    isExternalTicketing: false,
    externalTicketUrl: '',
    externalTicketDisplayPrice: '',
  });

  const [numberOfGuests, setNumberOfGuests] = useState('');
  // Phase 10: free events can greenlight on an RSVP headcount instead of cash —
  // offered only to hosts who've run a Party Starter before.
  const [rsvpEligible, setRsvpEligible] = useState(false);
  useEffect(() => {
    let cancelled = false;
    eventService.getRsvpGoalEligibility().then((r) => {
      if (!cancelled && r.success) setRsvpEligible(!!r.data?.eligible);
    });
    return () => { cancelled = true; };
  }, []);
  const [maxAttendees, setMaxAttendees] = useState('');
  const lastEditedField = useRef(null);

  // From a chat (plan detection): the suggestion's values land once, before the first paint.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!prefill || prefilledRef.current) return;
    prefilledRef.current = true;
    setFormData((prev) => ({ ...prev, ...(prefill.formData || {}) }));
    if (prefill.isFree != null) setIsFreeEvent(!!prefill.isFree);
    if (prefill.maxAttendees) setMaxAttendees(String(prefill.maxAttendees));
    if (prefill.partySupplies?.length) setPartySupplies(prefill.partySupplies.map((text) => ({ text, existingImageUrl: null, imageFile: null })));
  }, [prefill]);
  const fieldTag = (key) => (prefill?.tags?.[key] ? { tag: prefill.tags[key], tagTone: tagTone(prefill.tags[key]), quote: prefill.quotes?.[key] || null } : {});

  // Featured Wall region preview — ask the server which region this event's
  // coordinates land in (/regions/for-coords runs the real box check server-side).
  const [previewRegion, setPreviewRegion] = useState(null);
  const [regionChecked, setRegionChecked] = useState(false);
  useEffect(() => {
    const lat = formData.locationLat;
    const lng = formData.locationLng;
    if (lat == null || lng == null) { setPreviewRegion(null); setRegionChecked(false); return undefined; }
    let cancelled = false;
    (async () => {
      const r = await regionService.forCoords(lat, lng);
      if (cancelled) return;
      // A 404 is a real answer: no region covers this spot yet.
      setPreviewRegion(r.success ? r.data : null);
      setRegionChecked(true);
    })();
    return () => { cancelled = true; };
  }, [formData.locationLat, formData.locationLng]);

  // New events start PAID: the host names a price, and flips FREE EVENT on only when the
  // night really is free. Edit mode overrides this from the loaded event.
  const [isFreeEvent, setIsFreeEvent] = useState(false);
  const [flakeFeeMode, setFlakeFeeMode] = useState('percentage');
  const [flakeFeePercent, setFlakeFeePercent] = useState('6');
  const [flakeFeeFixed, setFlakeFeeFixed] = useState('');
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [existingPosterUrl, setExistingPosterUrl] = useState(null);
  const [eventStatus, setEventStatus] = useState(null);
  const [eventPaidCount, setEventPaidCount] = useState(0);

  // House Rules & Party Supplies & Volunteer Tasks
  const [houseRules, setHouseRules] = useState([]);
  const [partySupplies, setPartySupplies] = useState([]);
  const [volunteerTasks, setVolunteerTasks] = useState([]);
  const [links, setLinks] = useState([]); // [{url, text}]
  // Embedded (one form): the money and extras fold away and open on demand (or auto-open
  // in edit mode when they hold data).
  const [moneyOpen, setMoneyOpen] = useState(false);
  const [focusInvalidField, setFocusInvalidField] = useState(null);
  const moneyAutoOpened = useRef(false);
  useEffect(() => {
    if (!embedded || moneyAutoOpened.current) return;
    if (Object.keys(basicsErrors(formData)).length === 0 && Object.keys(placeErrors(formData)).length === 0) {
      moneyAutoOpened.current = true;
      setMoneyOpen(true);
    }
  }, [embedded, formData]);
  useEffect(() => {
    if (!focusInvalidField) return;
    const field = document.getElementById(`field-${focusInvalidField}`);
    field?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    field?.querySelector('input,select,textarea,button')?.focus({ preventScroll: true });
    setFocusInvalidField(null);
  }, [focusInvalidField, moneyOpen]);
  const [extrasOpen, setExtrasOpen] = useState(false);
  // Wizard step. 1 Basics · 2 Place · 3 Ticketing · 4 Extras · 5 Review.
  const [stepState, setStepState] = useState(1);
  // Inline (EDIT tab) the parent owns the step; standalone it is local state.
  const step = inline && stepProp ? stepProp : stepState;
  const setStep = (n) => { if (inline) onStepChange?.(n); else setStepState(n); };
  const [errors, setErrors] = useState({});
  const [stepTick, setStepTick] = useState(0);
  // Simple / Advanced (create mode only). Two step counters, so each view keeps its own place.
  // The view is a real path — /host/events/create is SIMPLE, /host/events/create/advanced is the
  // five-step wizard — so a host can bookmark or share either one. `?view=` still lands (old links)
  // and is rewritten to the path below.
  const viewFromQuery = searchParams.get('view');
  const viewFromUrl = viewFromPath === 'advanced' || viewFromQuery === 'advanced' ? 'advanced'
    : viewFromPath === 'simple' || viewFromQuery === 'simple' ? 'simple' : null;
  const [view, setView] = useState(viewFromUrl === 'advanced' || startAsPartyStarter ? 'advanced' : viewFromUrl || defaultView);
  const [simpleStep, setSimpleStep] = useState(1);
  const [tipOpen, setTipOpen] = useState(false);
  const { toast, say } = useToast();
  const simpleActive = !embedded && !inline && (!isEditMode || eventStatus === 'draft') && view === 'simple';
  useEffect(() => { setStepTick((t) => t + 1); }, [step, simpleStep]);
  useEffect(() => {
    if (!tipOpen) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setTipOpen(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [tipOpen]);
  // Only standalone create has a path to write: a reopened draft lives at /host/events/:id, where
  // there is no segment to own, so there the view stays state. The rewrite is a REPLACE — BACK
  // still leaves the creator instead of undoing a tab — and it strips the legacy `?view=`.
  const viewIsRouted = !embedded && !inline && !isEditMode;
  const searchStr = searchParams.toString();
  useEffect(() => {
    if (!viewIsRouted) return;
    const wantSeg = view === 'advanced' ? 'advanced' : undefined;
    if (viewFromPath === wantSeg && !searchParams.has('view')) return;
    const q = new URLSearchParams(searchStr);
    q.delete('view');
    navigate(`${EVENT_CREATE_PATH}${wantSeg ? `/${wantSeg}` : ''}${q.toString() ? `?${q}` : ''}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewIsRouted, view, viewFromPath, searchStr]);
  // A link straight to the other view while this page is already mounted (and BACK / FORWARD).
  const lastViewSeg = useRef(viewFromPath);
  useEffect(() => {
    if (!viewIsRouted || viewFromPath === lastViewSeg.current) return;
    lastViewSeg.current = viewFromPath;
    setView(viewFromPath === 'advanced' ? 'advanced' : 'simple');
  }, [viewIsRouted, viewFromPath]);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // The event as the server returned it (edit / inline) — seeds PUT IT ON A PAGE
  // (`clubIds`, `proPageIds`). Null while creating.
  const [loadedEvent, setLoadedEvent] = useState(null);
  // What LinkPagesPanel has the draft listed on: { clubIds, clubNames, proPageIds,
  // proPageNames, queryClubHandled }. Drives the Review row and lets the publish
  // path skip its ?clubId= link when the panel already made it.
  const [listedOn, setListedOn] = useState(null);
  // Bumped when the review step reorders photos, so the step-2 gallery re-reads its list.
  const [mediaVersion, setMediaVersion] = useState(0);

  // Auto-save hook
  const { saveStatus, triggerSave, autoSaveEventId, markSynced } = useAutoSave({
    eventId,
    formData,
    houseRules,
    partySupplies,
    volunteerTasks,
    isPriceLocked: isEditMode && eventPaidCount > 0,
    maxAttendees,
  });

  // Update URL silently when auto-save creates a draft (no remount/refetch).
  // Skipped when embedded (e.g. the Gathering wizard) so it doesn't hijack the
  // host page's URL from inside a modal.
  useEffect(() => {
    if (!isEditMode && !embedded && autoSaveEventId) {
      window.history.replaceState(null, '', `/host/events/${autoSaveEventId}/edit`);
    }
  }, [autoSaveEventId, isEditMode, embedded]);

  // Auto-upload poster when file selected and event exists
  const handlePosterAutoUpload = useCallback(async (file) => {
    setPosterFile(file);
    setErrors((prev) => ({ ...prev, poster: null }));
    const targetId = eventId || autoSaveEventId;
    if (file && targetId) {
      const result = await eventService.uploadPoster(targetId, file);
      if (result.success) {
        setExistingPosterUrl(result.data?.posterUrl || null);
      } else {
        setErrors((prev) => ({ ...prev, poster: 'Poster upload failed' }));
      }
    }
  }, [eventId, autoSaveEventId]);

  // A local preview of the chosen poster until the upload answers.
  useEffect(() => {
    if (!posterFile || typeof URL === 'undefined' || !URL.createObjectURL) { setPosterPreview(null); return undefined; }
    const url = URL.createObjectURL(posterFile);
    setPosterPreview(url);
    return () => { try { URL.revokeObjectURL(url); } catch { /* jsdom */ } };
  }, [posterFile]);

  // Fetch existing event data if editing.
  useEffect(() => {
    if (!isEditMode) return;

    const fetchEvent = async () => {
      setIsLoading(true);
      try {
        const result = await eventService.getEvent(eventId);
        if (result.success && result.data) {
          const event = result.data;

          // Edit gate: only the primary host or a co-host granted edit access may
          // edit. `viewerCanEdit` is computed server-side per requesting user, so
          // it's the authoritative check — a stranger (or a view-only co-host) is
          // refused the form rather than letting them fill it out and 404 on save.
          // (Backend `UpdateEventAsync` enforces the same rule on the write itself.)
          if (!event.viewerCanEdit) {
            await showAlert({
              title: 'Can’t edit this event',
              message: 'You don’t have permission to edit this event.',
              type: 'error',
            });
            navigate(`/events/${event.inviteCode}`, { replace: true });
            return;
          }
          setLoadedEvent(event);

          // Convert UTC datetime to local format for datetime-local input
          const toLocalInputValue = (utcString) => {
            const d = new Date(utcString);
            if (!utcString || d.getFullYear() <= 1900) return '';
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          };

          const dateTimeLocal = toLocalInputValue(event.dateTime);
          const endDateTimeLocal = toLocalInputValue(event.endDateTime);
          const fundingDeadlineLocal = toLocalInputValue(event.fundingDeadline);

          // A pay-what-you-want event with a $0 floor is NOT a free event — keep
          // it in paid/PWYW mode so editing doesn't silently strip the pricing.
          const eventIsFree = event.ticketPriceCents === 0 && event.fundingGoalCents === 0 && event.pricingMode !== 'pay_what_you_want';
          setIsFreeEvent(eventIsFree);
          setFormData({
            title: event.title || '',
            fundingGoal: eventIsFree ? '0' : (event.fundingGoalCents ? (event.fundingGoalCents / 100).toString() : ''),
            ticketPrice: eventIsFree ? '0' : (event.ticketPriceCents ? (event.ticketPriceCents / 100).toString() : ''),
            suggestedPrice: event.suggestedPriceCents ? (event.suggestedPriceCents / 100).toString() : '',
            pricingMode: event.pricingMode || 'fixed',
            description: event.description || '',
            dateTime: dateTimeLocal,
            endDateTime: endDateTimeLocal,
            fundingDeadline: fundingDeadlineLocal,
            location: event.locationAddress || '',
            locationLat: event.locationLatitude || null,
            locationLng: event.locationLongitude || null,
            locationCity: event.locationCity || '',
            venueName: event.venueName || '',
            venueAddress: event.venueAddress || '',
            venueLat: event.venueLat ?? null,
            venueLng: event.venueLng ?? null,
            mode: event.mode || 'open',
            fundingType: event.fundingType || 'guaranteed',
            rsvpGoal: event.rsvpGoal ? String(event.rsvpGoal) : '',
            category: event.category || 'other',
            categories: (event.categories && event.categories.length)
              ? event.categories
              : parseCategories(event.category, 'other'),
            customCategory: event.customCategory || '',
            isPrivate: !!event.isPrivate,
            giveTakeEnabled: event.giveTakeEnabled !== false,
            isExternalTicketing: !!event.isExternalTicketing,
            externalTicketUrl: event.externalTicketUrl || '',
            externalTicketDisplayPrice: event.externalTicketDisplayPrice || '',
          });

          // Derive number of guests from goal / price
          if (eventIsFree) {
            setNumberOfGuests('0');
          } else if (event.ticketPriceCents > 0 && event.fundingGoalCents > 0) {
            setNumberOfGuests(Math.ceil(event.fundingGoalCents / event.ticketPriceCents).toString());
          }
          if (event.maxAttendees) setMaxAttendees(event.maxAttendees.toString());
          setExistingPosterUrl(event.posterUrl);
          setEventStatus(event.status);
          setEventPaidCount(event.paidCount || 0);
          // A reopened draft that already uses Advanced-only settings opens in Advanced.
          if (event.status === 'draft' && viewFromQuery !== 'simple' && carriedSettings({
            fundingType: event.fundingType, mode: event.mode, isPrivate: event.isPrivate, isExternalTicketing: event.isExternalTicketing, pricingMode: event.pricingMode,
          }, event).length > 0) setView('advanced');

          // Auto-open the folded sections (embedded) when they already hold data.
          if (!eventIsFree || event.isExternalTicketing) setMoneyOpen(true);

          // Populate flake fee settings
          if (event.flakeFeeMode) setFlakeFeeMode(event.flakeFeeMode);
          if (event.flakeFeePercentage != null) setFlakeFeePercent(event.flakeFeePercentage.toString());
          if (event.flakeFeeFixedCents != null && event.flakeFeeFixedCents > 0) setFlakeFeeFixed((event.flakeFeeFixedCents / 100).toString());

          // Populate house rules
          if (event.houseRules?.length > 0) {
            setHouseRules(event.houseRules.map((r) => r.text));
            setExtrasOpen(true);
          }
          if (event.links?.length > 0) { setLinks(event.links); setExtrasOpen(true); }
          // Carry forward any legacy party supplies / volunteer tasks (now
          // superseded by the Give/Take board, kept dormant for back-compat).
          if (event.partySupplies?.length > 0) {
            const unclaimed = event.partySupplies.filter((s) => !s.isClaimed);
            setPartySupplies(unclaimed.map((s) => ({ text: s.text, existingImageUrl: s.imageUrl || null, imageFile: null })));
          }
          if (event.volunteerTasks?.length > 0) {
            const unclaimed = event.volunteerTasks.filter((v) => !v.isClaimed);
            setVolunteerTasks(unclaimed.map((v) => ({ text: v.text, existingImageUrl: v.imageUrl || null, imageFile: null })));
          }

          // Mark form as synced so auto-save doesn't fire immediately after load
          setTimeout(markSynced, 0);
        } else {
          setSubmitError('Failed to load event');
        }
      } catch (err) {
        setSubmitError('Failed to load event');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isEditMode]);

  // Price is locked once tickets have been sold (not just when published)
  const paidCount = paidCountProp ?? eventPaidCount;
  const isPriceLocked = isEditMode && paidCount > 0;

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field error when user starts typing
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };
  const setField = (name, value) => handleChange({ target: { name, value } });
  const setAndSave = (patch) => { setFormData((prev) => ({ ...prev, ...patch })); setTimeout(triggerSave, 0); };

  // Round up to nearest cent to prevent underfunding
  const ceilToCents = (val) => Math.ceil(val * 100) / 100;

  // Linked calculator logic for goal, guests, and per-person price
  const handleCalculatorChange = (field, rawValue) => {
    lastEditedField.current = field;

    if (field === 'numberOfGuests') {
      const intVal = rawValue === '' ? '' : parseInt(rawValue, 10);
      if (rawValue !== '' && (isNaN(intVal) || intVal < 0)) return;
      setNumberOfGuests(rawValue === '' ? '' : intVal.toString());
      if (errors.numberOfGuests) setErrors((prev) => ({ ...prev, numberOfGuests: null }));
      // Recalculate ticket price = goal / guests
      const goal = parseFloat(formData.fundingGoal);
      if (rawValue !== '' && intVal > 0 && !isNaN(goal) && goal > 0) {
        const newPrice = ceilToCents(goal / intVal);
        setFormData((prev) => ({ ...prev, ticketPrice: newPrice.toString() }));
      }
      return;
    }

    // For fundingGoal and ticketPrice, update formData
    setFormData((prev) => ({ ...prev, [field]: rawValue }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));

    const numVal = parseFloat(rawValue);
    const guests = parseInt(numberOfGuests, 10);

    if (field === 'fundingGoal') {
      // Recalculate ticket price = goal / guests
      if (rawValue !== '' && !isNaN(numVal) && numVal >= 0 && !isNaN(guests) && guests > 0) {
        if (numVal === 0) setFormData((prev) => ({ ...prev, ticketPrice: '0' }));
        else setFormData((prev) => ({ ...prev, ticketPrice: ceilToCents(numVal / guests).toString() }));
      }
    } else if (field === 'ticketPrice') {
      // Recalculate goal = price * guests
      if (rawValue !== '' && !isNaN(numVal) && numVal >= 0 && !isNaN(guests) && guests > 0) {
        const newGoal = Math.round(numVal * guests * 100) / 100;
        setFormData((prev) => ({ ...prev, fundingGoal: newGoal.toString() }));
      }
    }
  };

  // Validate form. Returns the new errors object (empty = valid). One validator for the wizard
  // and the from-chat review (utils/eventValidation); the sheet also needs a PICKED location.
  const validateForm = () => {
    const newErrors = validateEventForm(formData, { isFreeEvent, numberOfGuests, rsvpEligible, requireCoords: sheet });
    setErrors(newErrors);
    return newErrors;
  };

  // Convert dollars to cents
  const dollarsToCents = (dollars) => Math.round(parseFloat(dollars) * 100);

  const goStep = (n) => {
    setStep(Math.min(5, Math.max(1, n)));
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* jsdom */ }
  };
  const goSimple = (n) => {
    setSimpleStep(n === 2 ? 2 : 1);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* jsdom */ }
  };
  // Simple ↔ Advanced keeps every value; only the step maps: THE EVENT ↔ BASICS, TICKETS ↔ TICKETING.
  const switchView = (next) => {
    if (next === view) return;
    setTipOpen(false);
    if (next === 'advanced') setStep(simpleStep === 1 ? 1 : 3);
    else setSimpleStep(step <= 2 ? 1 : 2);
    setView(next);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* jsdom */ }
  };

  // Submit form
  const handleSubmit = async (publish = false) => {
    const newErrors = validateForm();
    const errorKeys = FIELD_ORDER.filter((k) => newErrors[k]);
    if (errorKeys.length > 0) {
      if (simpleActive) {
        // Simple: the inline field errors are enough, no summary note. A step-1 field sends the host
        // back to THE EVENT; only the price keeps them on TICKETS; a field Simple never shows (a
        // carried Party Starter's goal, say) sends them to Advanced at that field's step.
        setSubmitError(null);
        const hidden = errorKeys.find((k) => !SIMPLE_FIELD_STEP[k]);
        if (errorKeys.some((k) => SIMPLE_FIELD_STEP[k] === 1)) goSimple(1);
        else if (hidden) { setView('advanced'); goStep(FIELD_STEP[hidden] || 3); say('FINISH THE ADVANCED SETTINGS'); }
        onInvalid?.(newErrors);
        return;
      }
      const summary = `${errorKeys.map((k) => `${FIELD_LABELS[k] || k}: ${newErrors[k]}`).join('. ')}.`;
      setSubmitError(summary);
      // Jump to the step holding the first error so the field is visible.
      if (!embedded) goStep(FIELD_STEP[errorKeys[0]] || 1);
      else {
        if (errorKeys.some((key) => FIELD_STEP[key] === 3)) setMoneyOpen(true);
        if (errorKeys.some((key) => FIELD_STEP[key] === 4)) setExtrasOpen(true);
        setFocusInvalidField(errorKeys[0]);
      }
      onInvalid?.(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Build event data
      const filteredRules = houseRules.filter((r) => r.trim());
      const filteredSupplies = partySupplies.filter((s) => s.text.trim());
      const filteredVolunteers = volunteerTasks.filter((v) => v.text.trim());
      const eventData = {
        title: formData.title.trim(),
        fundingGoalCents: isFreeEvent ? 0 : dollarsToCents(formData.fundingGoal),
        description: formData.description.trim(),
        dateTime: new Date(formData.dateTime).toISOString(),
        endDateTime: formData.endDateTime ? new Date(formData.endDateTime).toISOString() : null,
        // Guaranteed events don't crowdfund — backfill the deadline with the event date
        // when the host hasn't picked one (the field is hidden in the UI in that mode).
        fundingDeadline: (formData.fundingDeadline
          ? new Date(formData.fundingDeadline)
          : new Date(formData.dateTime)).toISOString(),
        locationAddress: formData.location.trim(),
        locationLatitude: formData.locationLat,
        locationLongitude: formData.locationLng,
        locationCity: formData.locationCity,
        venueName: (formData.venueName || '').trim(),
        venueAddress: (formData.venueAddress || '').trim(),
        venueLat: formData.venueName ? formData.venueLat : null,
        venueLng: formData.venueName ? formData.venueLng : null,
        mode: formData.mode,
        // Free and external events "just happen" (guaranteed) — UNLESS the host opts a
        // free event into an RSVP-goal Party Starter (Phase 10): only happens if enough
        // people RSVP. That path is gated on eligibility + a headcount goal.
        ...(() => {
          const rsvpGoalOn = isFreeEvent && !formData.isExternalTicketing
            && formData.fundingType === 'party_starter' && rsvpEligible
            && parseInt(formData.rsvpGoal, 10) > 0;
          return {
            fundingType: formData.isExternalTicketing
              ? 'guaranteed'
              : (isFreeEvent ? (rsvpGoalOn ? 'party_starter' : 'guaranteed') : formData.fundingType),
            goalType: rsvpGoalOn ? 'rsvp' : 'cash',
            rsvpGoal: rsvpGoalOn ? parseInt(formData.rsvpGoal, 10) : null,
          };
        })(),
        category: formData.categories?.[0] || formData.category || 'other',
        categories: formData.categories,
        customCategory: formData.customCategory,
        isPrivate: !!formData.isPrivate,
        giveTakeEnabled: formData.giveTakeEnabled !== false,
        isExternalTicketing: !!formData.isExternalTicketing,
        externalTicketUrl: formData.isExternalTicketing ? (formData.externalTicketUrl || '').trim() : '',
        externalTicketDisplayPrice: formData.isExternalTicketing ? (formData.externalTicketDisplayPrice || '').trim() : '',
        houseRules: filteredRules.length > 0 ? filteredRules : [],
        partySupplies: filteredSupplies.length > 0 ? filteredSupplies.map((s) => s.text.trim()) : [],
        volunteerTasks: filteredVolunteers.length > 0 ? filteredVolunteers.map((v) => v.text.trim()) : [],
        maxAttendees: maxAttendees ? parseInt(maxAttendees, 10) : 0,
        links,
      };

      // Only include price for draft, non-external events (backend zeroes pricing for external).
      if (!isPriceLocked && !formData.isExternalTicketing) {
        eventData.ticketPriceCents = isFreeEvent ? 0 : dollarsToCents(formData.ticketPrice);
        eventData.pricingMode = isFreeEvent ? 'fixed' : (formData.pricingMode || 'fixed');
        if (eventData.pricingMode === 'pay_what_you_want' && formData.suggestedPrice) {
          eventData.suggestedPriceCents = dollarsToCents(formData.suggestedPrice);
        } else {
          eventData.suggestedPriceCents = null;
        }
        // Include flake fee settings (only editable in draft). Guaranteed events
        // have no withdrawal path, so never carry a fee over from a draft that
        // started as a Party Starter.
        if (!isFreeEvent && formData.fundingType === 'party_starter') {
          eventData.flakeFeeMode = flakeFeeMode;
          eventData.flakeFeePercentage = flakeFeeMode === 'percentage' ? parseInt(flakeFeePercent, 10) || 6 : 0;
          eventData.flakeFeeFixedCents = flakeFeeMode === 'fixed' ? dollarsToCents(flakeFeeFixed || '0') : 0;
        } else {
          eventData.flakeFeeMode = 'none';
          eventData.flakeFeePercentage = 0;
          eventData.flakeFeeFixedCents = 0;
        }
      }

      let result;
      let targetEventId = eventId || autoSaveEventId;

      if (targetEventId) {
        // Update existing event (edit mode or auto-saved draft)
        result = await eventService.updateEvent(targetEventId, eventData);
        if (!result.success) throw new Error(result.error || 'Failed to update event');
      } else {
        // Create new event
        result = await eventService.createEvent(eventData);
        if (!result.success) throw new Error(result.error || 'Failed to create event');
        targetEventId = result.data.id;
      }

      // Upload poster if provided
      let hadUploadErrors = false;
      if (posterFile) {
        const posterResult = await eventService.uploadPoster(targetEventId, posterFile);
        if (!posterResult.success) {
          setSubmitError(`Event saved, but poster upload failed: ${posterResult.error}`);
          hadUploadErrors = true;
        }
      }

      // Upload party supply images if any
      if (result.data?.partySupplies?.length > 0) {
        const supplyImageErrors = [];
        for (let i = 0; i < filteredSupplies.length; i++) {
          if (filteredSupplies[i].imageFile && result.data.partySupplies[i]) {
            const imgResult = await eventService.uploadPartySupplyImage(result.data.partySupplies[i].id, filteredSupplies[i].imageFile);
            if (!imgResult.success) {
              console.error(`Supply image upload failed for index ${i}:`, imgResult.error);
              supplyImageErrors.push(filteredSupplies[i].text);
            }
          }
        }
        if (supplyImageErrors.length > 0) {
          setSubmitError(`Event saved, but image upload failed for: ${supplyImageErrors.join(', ')}`);
          hadUploadErrors = true;
        }
      }

      // Upload volunteer task images if any
      if (result.data?.volunteerTasks?.length > 0) {
        const taskImageErrors = [];
        for (let i = 0; i < filteredVolunteers.length; i++) {
          if (filteredVolunteers[i].imageFile && result.data.volunteerTasks[i]) {
            const imgResult = await eventService.uploadVolunteerTaskImage(result.data.volunteerTasks[i].id, filteredVolunteers[i].imageFile);
            if (!imgResult.success) {
              console.error(`Volunteer task image upload failed for index ${i}:`, imgResult.error);
              taskImageErrors.push(filteredVolunteers[i].text);
            }
          }
        }
        if (taskImageErrors.length > 0) {
          setSubmitError(`Event saved, but image upload failed for: ${taskImageErrors.join(', ')}`);
          hadUploadErrors = true;
        }
      }

      // If publishing, call publish endpoint
      if (publish && result.data.status === 'draft') {
        const publishResult = await eventService.publishEvent(targetEventId);
        if (!publishResult.success) {
          const errorMsg = publishResult.error?.message || publishResult.error || 'Failed to publish event';
          throw new Error(errorMsg);
        }
      }

      // Don't navigate if there were upload errors — let user see the message
      if (hadUploadErrors) return;

      // Auto-link to club if created from a club page / the gathering wizard —
      // unless PUT IT ON A PAGE already linked it against the draft (one call, and
      // a person who switched it off in the panel meant it).
      if (clubIdFromQuery && targetEventId && !isEditMode && !listedOn?.queryClubHandled) {
        await clubService.linkEventToClub(clubIdFromQuery, targetEventId);
      }
      // Attach to a gathering agenda when the builder is embedded in the wizard.
      // (Club link must land first — attach requires the event's ClubId to match.)
      if (gatheringId && targetEventId && !isEditMode) {
        await gatheringService.attachEvent(gatheringId, targetEventId);
      }

      // Embedded (wizard) mode hands control back to the host instead of navigating.
      if (embedded) {
        onComplete?.(targetEventId, result.data);
        return;
      }
      // Inline (host dashboard EDIT tab): the dashboard refetches; no navigation.
      if (inline) {
        onSaved?.(targetEventId, result.data);
        return;
      }

      // Navigate based on mode
      if (clubIdFromQuery && !isEditMode) {
        navigate(`/clubs/${clubIdFromQuery}`);
      } else if (isEditMode && eventStatus !== 'draft') {
        navigate(`/host/events/${eventId}`);
      } else {
        navigate(`/host/events/${targetEventId}/share`, {
          state: { published: publish, event: result.data },
        });
      }
    } catch (error) {
      console.error('Failed to save event:', error);
      setSubmitError(error.message || 'Failed to save event. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Inline: let the dashboard call this builder's submit.
  useEffect(() => {
    if (saveRef) saveRef.current = () => handleSubmit(false);
  });

  // Handle delete event
  const handleDelete = async () => {
    const confirmed = await showConfirm({
      title: 'DELETE EVENT',
      message: 'Are you sure you want to delete this event? This cannot be undone.',
      confirmLabel: 'DELETE',
    });
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      const result = await eventService.deleteEvent(eventId);
      if (!result.success) throw new Error(result.error || 'Failed to delete event');
      navigate('/host');
    } catch (error) {
      setSubmitError(error.message || 'Failed to delete event.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="hev" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Card center gap="12" style={{ maxWidth: 420, width: '100%' }}>
          <span className="hev-sec-title">AUTHENTICATION REQUIRED</span>
          <span className="hev-sec-sub">Please sign in to {isEditMode ? 'edit' : 'create'} an event.</span>
          <Btn onClick={() => navigate('/login')}>SIGN IN</Btn>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={embedded || inline ? 'hev' : 'hev'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, minHeight: embedded || inline ? 0 : undefined }}>
        <LoadingSpinner label="Loading event..." />
      </div>
    );
  }

  // ── Derived ──
  const standalone = !embedded && !inline;
  const isWizard = !embedded;
  // Create mode: a brand new event, or a draft reopened at /host/events/:id/edit — the
  // footer offers SAVE DRAFT + PUBLISH either way. Inline is always edit mode.
  const createMode = standalone && (!isEditMode || eventStatus === 'draft');
  const simple = simpleActive;
  const carry = carriedSettings(formData, loadedEvent);
  const draftId = eventId || autoSaveEventId;
  const ext = !!formData.isExternalTicketing;
  const free = isFreeEvent && !ext;
  const starter = formData.fundingType === 'party_starter' && !ext;
  const price = parseFloat(formData.ticketPrice) || 0;
  const goal = parseFloat(formData.fundingGoal) || 0;
  const guests = parseInt(numberOfGuests, 10) || 0;
  const pwyw = formData.pricingMode === 'pay_what_you_want';
  const lock = isPriceLocked;
  const saving = saveStatus === 'saving' || isSubmitting;
  const disabled = isSubmitting;

  const moneyWord = ext ? 'EXTERNAL' : free ? 'FREE' : starter ? 'PARTY STARTER' : 'GUARANTEED';
  const moneyStatus = ext ? 'External tickets'
    : free ? (starter && rsvpEligible ? 'Free · RSVP goal' : 'Free')
      : `${starter ? 'Party Starter' : 'Guaranteed'} · ${usd(price)}${pwyw ? ' minimum, pay what you want' : ''}${starter && goal ? ` · goal ${usd(goal)}` : ''}`;

  const fundingHelp = free
    ? 'This is a free event — no charges, no fees, no escrow. Guests just RSVP to grab a spot.'
    : formData.fundingType === 'guaranteed'
      ? 'You set your price. You get your price. We add $1 on top for your guests — never comes out of your earnings. Funds available 24 hours after your event.'
      : 'You set your price. If your event greenlights, Portal takes 7% — that’s what covers the escrow and the guarantee. We add $1 on top for your guests. Funds available 24 hours after your event.';

  const hasSummary = !free && !ext && price > 0 && (formData.fundingType === 'guaranteed' || (goal > 0 && guests > 0));
  const summaryLine = starter
    ? `Your gathering funds at ${usd(goal)} total with ${guests} guest${guests !== 1 ? 's' : ''} contributing ${usd(price)} each.`
    : `Each guest pays ${usd(price)}.`;
  const flakeEst = flakeFeeMode === 'percentage' && price > 0 && flakeFeePercent
    ? `≈ ${usd(price * (parseInt(flakeFeePercent, 10) || 0) / 100)} per ticket` : '';

  // Step-level validity (Basics + Place gate CONTINUE and, in create mode, the later steps).
  const step1Errors = () => basicsErrors(formData);
  const step2Errors = () => placeErrors(formData, { requireCoords: sheet });
  const basicsComplete = Object.keys(step1Errors()).length === 0 && Object.keys(step2Errors()).length === 0;
  const stepHasErrors = (n) => Object.keys(errors).some((k) => errors[k] && FIELD_STEP[k] === n);

  const wizardContinue = () => {
    if (step === 1) { const er = step1Errors(); if (Object.keys(er).length) { setErrors((p) => ({ ...p, ...er })); onInvalid?.(er); return; } }
    if (step === 2) { const er = step2Errors(); if (Object.keys(er).length) { setErrors((p) => ({ ...p, ...er })); onInvalid?.(er); return; } }
    goStep(Math.min(5, step + 1));
  };

  const show = (n) => !isWizard || step === n;
  // A card's props when it is (or isn't) the step on screen: hidden cards stay mounted so the
  // leaf editors load once and the review keeps its LISTED ON report; the visible one replays
  // the enter animation (alternating between two identical keyframes restarts it).
  const stepCard = (n) => ({ hidden: !show(n), style: { animationName: stepTick % 2 ? 'hevRiseB' : 'hevRise' } });
  const ruleCount = houseRules.filter((r) => r.trim()).length;
  const extrasBits = [
    ruleCount ? `${ruleCount} rules` : null,
    links.length ? `${links.length} links` : null,
    formData.giveTakeEnabled ? 'Give/Take' : null,
  ].filter(Boolean);
  const listedNames = [...(listedOn?.clubNames || []), ...(listedOn?.proPageNames || [])].filter(Boolean);

  const saveState = saveStatus === 'saving' ? 'saving' : (draftId ? 'saved' : 'idle');
  const posterShown = posterPreview || existingPosterUrl;

  // ── Pieces ──
  const wizardTop = isWizard && (
    <div className="hev-wiz-top" data-testid="wizard-top">
      <span className="hev-wiz-kick">{createMode ? 'NEW EVENT' : 'EDIT EVENT'}</span>
      <span className="hev-wiz-note" data-testid="edit-step-note">
        <span className="hev-dot" data-busy={saving ? '' : undefined} />
        STEP {simple ? simpleStep : step} / {simple ? 2 : 5}{simple ? ' · SIMPLE' : createMode ? '' : ' · EVERY STEP IS UNLOCKED'}
      </span>
    </div>
  );

  const stepPills = isWizard && (
    <div className="hev-steps" role="tablist" aria-label="Edit steps" data-testid="edit-step-tabs">
      {STEP_LABELS.map((label, i) => {
        const n = i + 1;
        const on = step === n;
        const bad = stepHasErrors(n);
        const locked = createMode && !(n <= step || basicsComplete);
        return (
          <button key={label} type="button" role="tab" aria-selected={on} aria-label={`${n}. ${label}`} className="hev-step" data-on={on ? '' : undefined} data-bad={bad ? '' : undefined} disabled={locked} onClick={() => goStep(n)}>
            <span className="hev-step-n">{bad ? '!' : n}</span>
            <span className="hev-step-l">{label}</span>
          </button>
        );
      })}
    </div>
  );

  const viewSwitch = createMode && (
    <div className="hev-view" data-testid="view-switch">
      <div className="hev-view-tabs" role="tablist" aria-label="Builder view" onMouseLeave={() => setTipOpen(false)}>
        <button type="button" role="tab" aria-selected={view === 'simple'} className="hev-view-tab" data-on={view === 'simple' ? '' : undefined} onClick={() => switchView('simple')} data-testid="view-simple">SIMPLE</button>
        <button type="button" role="tab" aria-selected={view === 'advanced'} className="hev-view-tab" data-on={view === 'advanced' ? '' : undefined} onClick={() => switchView('advanced')} onMouseEnter={() => setTipOpen(true)} onFocus={() => setTipOpen(true)} onBlur={() => setTipOpen(false)} data-testid="view-advanced">ADVANCED</button>
        <button type="button" className="hev-view-q" aria-label="What’s in Advanced" aria-expanded={tipOpen} onClick={() => setTipOpen((o) => !o)} onMouseEnter={() => setTipOpen(true)} data-testid="view-help">?</button>
        {tipOpen && (
          <div className="hev-tip" role="tooltip" data-testid="advanced-tip">
            <span className="hev-tip-k">EVERYTHING IN SIMPLE, PLUS</span>
            <div className="hev-tip-rows">
              {[...ADVANCED_ROWS, ...(canUsePlanner ? [['PLAN IT FOR ME', 'Describe the event and let the planner price it.']] : [])].map(([l, t]) => (
                <React.Fragment key={l}><span className="hev-tip-l">{l}</span><span className="hev-tip-t">{t}</span></React.Fragment>
              ))}
            </div>
            <span className="hev-tip-foot">YOUR ANSWERS CARRY OVER BOTH WAYS.</span>
          </div>
        )}
      </div>
      <span className="hev-view-hint" data-testid="view-hint">{view === 'simple' ? 'Two steps. A guaranteed event with an open guestlist, listed publicly.' : 'Every setting, across five steps.'}</span>
    </div>
  );

  const simpleStepHasErrors = (n) => Object.keys(errors).some((k) => errors[k] && SIMPLE_FIELD_STEP[k] === n);
  const simplePills = simple && (
    <div className="hev-steps" role="tablist" aria-label="Steps" data-testid="simple-step-tabs">
      {SIMPLE_STEP_LABELS.map((label, i) => {
        const n = i + 1;
        const on = simpleStep === n;
        const bad = simpleStepHasErrors(n);
        const locked = n === 2 && simpleStep === 1 && !basicsComplete;
        return (
          <button key={label} type="button" role="tab" aria-selected={on} aria-label={`${n}. ${label}`} className="hev-step" data-on={on ? '' : undefined} data-bad={bad ? '' : undefined} disabled={locked} onClick={() => goSimple(n)}>
            <span className="hev-step-n">{bad ? '!' : n}</span>
            <span className="hev-step-l">{label}</span>
          </button>
        );
      })}
    </div>
  );

  const simpleContinue = () => {
    const er = { ...basicsErrors(formData), ...placeErrors(formData) };
    if (Object.keys(er).length) { setErrors((p) => ({ ...p, ...er })); onInvalid?.(er); return; }
    goSimple(2);
  };

  // Set only when this is a published event with a canonical URL — a draft has nothing shared yet.
  const linkMoves = isEditMode && loadedEvent && loadedEvent.status !== 'draft'
    && loadedEvent.citySlug && loadedEvent.slug
    ? eventPath(loadedEvent)
    : null;

  const basicsCard = (
    <Card gap="24" rise testId="step-basics" {...stepCard(1)}>
      <SectionHead title="THE BASICS" sub="All you need for a free night." tag="REQUIRED" />

      <Field label="EVENT NAME" required error={errors.title} htmlFor="ev-title" {...fieldTag('title')}>
        <TextInput id="ev-title" name="title" size="lg" placeholder="Enter event name" value={formData.title} onChange={handleChange} onBlur={triggerSave} error={!!errors.title} disabled={disabled} aria-label="Event Name" />
      </Field>

      <div className="hev-grid2">
        <Field label="DATE & TIME" required error={errors.dateTime} htmlFor="ev-date" {...fieldTag('dateTime')}>
          <TextInput id="ev-date" type="datetime-local" name="dateTime" value={formData.dateTime} onChange={handleChange} onBlur={triggerSave} error={!!errors.dateTime} disabled={disabled} />
        </Field>
        <Field label="END DATE & TIME" optional error={errors.endDateTime} htmlFor="ev-end" {...fieldTag('endDateTime')}>
          <TextInput id="ev-end" type="datetime-local" name="endDateTime" value={formData.endDateTime} min={formData.dateTime || undefined} onChange={handleChange} onBlur={triggerSave} error={!!errors.endDateTime} disabled={disabled} />
        </Field>
      </div>
      <Help style={{ marginTop: -14 }}>End time is optional — when the event wraps up. Leave blank to use the default 5-hour window.</Help>

      {/* The canonical URL is built from the name and (when the name is taken) the date, so
          editing either can move it. Every address the event has ever had keeps working — but a
          host who printed a QR code deserves to know the link they're looking at isn't frozen. */}
      {linkMoves && (
        <Note tag="THE LINK" tone="warning" testId="slug-move-warning" small>
          <span>
            This event lives at <b>{linkMoves}</b>. Its address is built from the name and date, so
            changing either can move it. Anything you've already shared — a poster, a QR code, a
            post — keeps working: every old address still opens this event.
          </span>
        </Note>
      )}

      <Field as="div" label="GUESTLIST TYPE" required {...fieldTag('mode')}>
        <div className="hev-grid2" data-gap="8" role="radiogroup" aria-label="Guestlist type">
          <ChoiceCard on={formData.mode === 'curated'} label="CURATED" hint="You will manually approve or decline each guest" onClick={() => setAndSave({ mode: 'curated' })} disabled={disabled} />
          <ChoiceCard on={formData.mode === 'open'} label="OPEN" hint="All guests are automatically accepted" onClick={() => setAndSave({ mode: 'open' })} disabled={disabled} />
        </div>
      </Field>

      <Field as="div" label="CATEGORIES" required error={errors.categories} id="field-categories" {...fieldTag('categories')}>
        <CategoryMultiSelect
          value={formData.categories}
          customValue={formData.customCategory}
          error={errors.categories}
          onChange={(slugs) => {
            setFormData((prev) => ({ ...prev, categories: slugs, category: slugs[0] || '' }));
            if (slugs.length > 0) setErrors((prev) => ({ ...prev, categories: null }));
            setTimeout(triggerSave, 0);
          }}
          onCustomChange={(text) => setAndSave({ customCategory: text })}
        />
        <Help>Pick up to 3. The first (★) is your primary — curators use it for the Featured Wall (1 per category per region).</Help>
      </Field>

      <ToggleRow label="PRIVATE EVENT" desc={prefill ? 'Hidden from the Featured Wall and all public listings. Suggested because the plan came from a private chat.' : 'Hidden from the Featured Wall and all public listings. Only people with the direct invite link can see it.'} on={!!formData.isPrivate} onToggle={() => setAndSave({ isPrivate: !formData.isPrivate })} disabled={disabled} testId="toggle-private" />
    </Card>
  );

  const placeCard = (
    <Card gap="24" rise testId="step-place" {...stepCard(2)}>
      <SectionHead title="PLACE & PITCH" sub="Where it is and what it’s about." tag="REQUIRED" />

      <Field as="div" label="LOCATION" required error={errors.location} id="field-location" {...fieldTag('location')}>
        <AddressAutocomplete
          label={null}
          value={formData.location}
          onSelect={({ address, latitude, longitude, city }) => {
            setFormData((prev) => ({ ...prev, location: address, locationLat: latitude, locationLng: longitude, locationCity: city }));
            setErrors((prev) => ({ ...prev, location: null }));
            setTimeout(triggerSave, 0);
          }}
          onChange={handleChange}
          onBlur={triggerSave}
          error={errors.location}
          disabled={disabled}
        />
        {previewRegion && <Help body>This event will appear in the <b>{previewRegion.name}</b> wall.</Help>}
        {!previewRegion && regionChecked && <Help>No matching region — this event will be browsable in the All Events drawer but won’t appear in any region’s Featured Strip.</Help>}
      </Field>

      <Field as="div" label="VENUE" optional hint="A named spot inside the location — a winery, a bar, a park." id="field-venue" {...fieldTag('venueName')}>
        <VenuePicker
          variant="pill"
          value={formData.venueName ? { name: formData.venueName, address: formData.venueAddress, lat: formData.venueLat, lng: formData.venueLng } : null}
          onChange={(v) => setAndSave({ venueName: v?.name || '', venueAddress: v?.address || '', venueLat: v?.lat ?? null, venueLng: v?.lng ?? null })}
          cityCoords={formData.locationLat != null && formData.locationLng != null ? { lat: formData.locationLat, lng: formData.locationLng } : null}
          cityLabel={formData.locationCity || formData.location}
          disabled={disabled}
        />
      </Field>

      <Field as="div" label="DESCRIPTION" required error={errors.description} id="field-description" {...fieldTag('description')}>
        <RichTextEditor
          placeholder="Describe your event… use headings, lists, and bold to make it easy to read."
          value={formData.description}
          onChange={(html) => {
            setFormData((prev) => ({ ...prev, description: html }));
            if (errors.description) setErrors((prev) => ({ ...prev, description: null }));
          }}
          onBlur={triggerSave}
          error={errors.description}
          disabled={disabled}
        />
      </Field>

      <div className={posterShown ? 'hev-grid2' : ''} style={{ alignItems: 'start' }}>
        {posterShown && (
          <Field as="div" label="CURRENT POSTER">
            <PosterSlot posterUrl={posterShown} alt="Event poster" className="hev-poster" padClass="p-0" blur={24} fallbackKey={draftId} />
          </Field>
        )}
        <Field as="div" label={posterShown ? 'REPLACE POSTER' : 'EVENT POSTER'} optional error={errors.poster}>
          <PosterDrop onFile={handlePosterAutoUpload} onError={(m) => setErrors((prev) => ({ ...prev, poster: m }))} disabled={disabled} />
        </Field>
      </div>

      <EventMediaManager eventId={draftId} refreshKey={mediaVersion} />
    </Card>
  );

  const externalPanel = ext && (
    <SubPanel title="EXTERNAL TICKETING" sub="Where should guests go to buy tickets?" gap="16" testId="external-panel">
      <Field label="TICKETING LINK" required error={errors.externalTicketUrl} htmlFor="ev-ext-url" id="field-externalTicketUrl">
        <TextInput id="ev-ext-url" type="url" white placeholder="https://eventbrite.com/e/your-event" value={formData.externalTicketUrl} onChange={(e) => setField('externalTicketUrl', e.target.value)} onBlur={triggerSave} error={!!errors.externalTicketUrl} disabled={disabled} />
      </Field>
      <Field label="DISPLAY PRICE" optional htmlFor="ev-ext-price" hint={<span className="hev-help" data-caps>SHOWN TO GUESTS FOR REFERENCE. WE DON’T PROCESS THIS AMOUNT.</span>}>
        <TextInput id="ev-ext-price" white maxLength={50} placeholder="e.g. From $25" value={formData.externalTicketDisplayPrice} onChange={(e) => setField('externalTicketDisplayPrice', e.target.value)} onBlur={triggerSave} disabled={disabled} />
      </Field>
    </SubPanel>
  );

  const toggleFree = () => {
    const next = !isFreeEvent;
    setIsFreeEvent(next);
    if (next) {
      setFormData((prev) => ({ ...prev, fundingGoal: '0', ticketPrice: '0' }));
      setNumberOfGuests('0');
      setErrors((prev) => ({ ...prev, fundingGoal: null, ticketPrice: null, numberOfGuests: null }));
    } else {
      setFormData((prev) => ({ ...prev, fundingGoal: '', ticketPrice: '' }));
      setNumberOfGuests('');
    }
    setTimeout(triggerSave, 0);
  };

  const ticketingCard = (
    <Card gap="24" rise testId="step-ticketing" {...stepCard(3)}>
      <SectionHead title="THE MONEY" sub="How the night funds." tag={moneyWord} tagTone="black" />

      {lock && <Note tag="LOCKED" tone="warning" testId="lock-banner">Tickets have sold. Ticketing, price, guest count and flake fee can’t change.</Note>}

      <Field as="div" label="TICKETING" required>
        <div className="hev-grid2" data-gap="8" role="radiogroup" aria-label="Ticketing">
          <ChoiceCard on={!ext} label="HOST ON PORTAL" hint="Sell tickets right here on Portal with built-in checkout." disabled={disabled || lock}
            onClick={() => { setFormData((prev) => ({ ...prev, isExternalTicketing: false })); setErrors((prev) => ({ ...prev, externalTicketUrl: null })); setTimeout(triggerSave, 0); }} />
          <ChoiceCard on={ext} label="EXTERNAL TICKETS" hint="Tickets are sold on another site. Portal lists the event and links guests out — no on-platform checkout or fees." disabled={disabled || lock}
            onClick={() => {
              // External events are billboards: force Guaranteed lifecycle, clear the
              // free-event flag, and drop any pricing errors that no longer apply.
              setIsFreeEvent(false);
              setFormData((prev) => ({ ...prev, isExternalTicketing: true, fundingType: 'guaranteed' }));
              setErrors((prev) => ({ ...prev, ticketPrice: null, fundingGoal: null, numberOfGuests: null, fundingDeadline: null }));
              setTimeout(triggerSave, 0);
            }} />
        </div>
      </Field>

      {externalPanel}

      {!ext && (
        <>
          <Field as="div" label="FUNDING TYPE" required>
            <div className="hev-grid3" role="radiogroup" aria-label="Funding type">
              <ChoiceCard on={formData.fundingType === 'guaranteed'} label="GUARANTEED" hint="The night runs no matter what. You get your price." onClick={() => setAndSave({ fundingType: 'guaranteed' })} disabled={disabled} />
              <ChoiceCard on={formData.fundingType === 'party_starter'} label="PARTY STARTER" hint="It only happens when the fund goal is hit." onClick={() => setAndSave({ fundingType: 'party_starter' })} disabled={disabled} />
              {canUsePlanner && <ChoiceCard dashed label="PLAN IT FOR ME" hint="Describe the event in one line and let the planner find the space, sound, crew, permits and price." onClick={() => navigate('/host/events/plan')} disabled={disabled} title="Describe the event in one line and let the planner find the space, sound, crew, permits and price." />}
            </div>
            <Help body>{fundingHelp}</Help>
          </Field>

          <ToggleRow label="FREE EVENT" lockTag={lock} desc="No ticket price or funding goal — guests attend for free" on={isFreeEvent} onToggle={toggleFree} disabled={disabled || lock} testId="toggle-free" />
        </>
      )}

      {free && starter && (rsvpEligible ? (
        <Field as="div" label="RSVP GOAL" required error={errors.rsvpGoal} id="field-rsvpGoal" hintAbove="This event only happens if enough people say they’re in. You greenlight it once the goal is hit.">
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <TextInput type="number" min="1" step="1" inputMode="numeric" value={formData.rsvpGoal} onChange={(e) => setField('rsvpGoal', e.target.value)} onBlur={() => setTimeout(triggerSave, 0)} placeholder="20" error={!!errors.rsvpGoal} style={{ width: 120 }} aria-label="RSVP goal" disabled={disabled} />
            <span className="hev-mono-caps" style={{ color: '#6B7280' }}>PEOPLE RSVP’D</span>
          </span>
        </Field>
      ) : (
        <Help>RSVP-goal parties (only happen if enough people commit) unlock after you’ve run a Party Starter. For now this saves as a regular free event.</Help>
      ))}

      {starter && (
        <Field label={free ? 'RSVP DEADLINE' : 'FUNDING DEADLINE'} required error={errors.fundingDeadline} htmlFor="ev-deadline" id="field-fundingDeadline" hintAbove={free ? 'When do RSVPs close?' : 'When does funding need to be locked in?'}>
          <TextInput id="ev-deadline" type="datetime-local" name="fundingDeadline" value={formData.fundingDeadline} onChange={handleChange} onBlur={triggerSave} error={!!errors.fundingDeadline} disabled={disabled} style={{ maxWidth: 360 }} />
        </Field>
      )}

      {!ext && !free && (
        <SubPanel title={starter ? 'SET YOUR EVENT FUND GOAL' : 'TICKETING'} sub={starter ? 'Define the number of guests and per-person contribution you need to make your event happen.' : 'Set your ticket price per guest.'} gap="16" testId="pricing-panel">
          {starter && (
            <div className="hev-grid2" data-gap="14">
              <Field label="EVENT FUND GOAL ($)" required error={errors.fundingGoal} htmlFor="ev-goal" id="field-fundingGoal">
                <AffixInput id="ev-goal" prefix="$" type="number" min="0" step="0.01" white placeholder="0.00" value={formData.fundingGoal} onChange={(e) => handleCalculatorChange('fundingGoal', e.target.value)} onBlur={triggerSave} error={!!errors.fundingGoal} disabled={disabled} />
              </Field>
              <Field label="NUMBER OF GUESTS" required lockTag={lock} error={errors.numberOfGuests} htmlFor="ev-guests" id="field-numberOfGuests">
                <TextInput id="ev-guests" type="number" min="0" step="1" white placeholder="0" value={numberOfGuests} onChange={(e) => handleCalculatorChange('numberOfGuests', e.target.value)} onBlur={triggerSave} error={!!errors.numberOfGuests} disabled={disabled || lock} />
              </Field>
            </div>
          )}
          <Field label="BASE TICKET PRICE ($)" required lockTag={lock} error={errors.ticketPrice} htmlFor="ev-price" id="field-ticketPrice" {...fieldTag('ticketPrice')}>
            <AffixInput id="ev-price" prefix="$" type="number" min="0" step="0.01" white placeholder="0.00" value={formData.ticketPrice} onChange={(e) => handleCalculatorChange('ticketPrice', e.target.value)} onBlur={triggerSave} error={!!errors.ticketPrice} disabled={disabled || lock} style={{ maxWidth: 280 }} />
          </Field>
          <ToggleRow white label="LET GUESTS PAY WHAT THEY WANT" desc="Guests pick any amount at or above the price above (used as the minimum)." on={pwyw} disabled={disabled || lock} testId="toggle-pwyw"
            onToggle={() => { const next = pwyw ? 'fixed' : 'pay_what_you_want'; setAndSave({ pricingMode: next, suggestedPrice: next === 'fixed' ? '' : formData.suggestedPrice }); }} />
          {pwyw && (
            <Field label="SUGGESTED PRICE" optional htmlFor="ev-suggested" hint={<span className="hev-help" data-caps>PREFILLED FOR GUESTS AS A STARTING POINT. THEY CAN CHANGE IT.</span>}>
              <AffixInput id="ev-suggested" prefix="$" type="number" min={formData.ticketPrice || 0} step="0.01" white placeholder="e.g. 25.00" value={formData.suggestedPrice} onChange={(e) => setField('suggestedPrice', e.target.value)} onBlur={triggerSave} disabled={disabled || lock} style={{ maxWidth: 280 }} />
            </Field>
          )}
          {hasSummary && <Note tone="black" testId="pricing-summary">{summaryLine}</Note>}
        </SubPanel>
      )}

      {!free && !ext && !pwyw && <TicketTiersSection eventId={draftId} />}

      {!free && !ext && <PromoCodesSection eventId={draftId} ticketPriceCents={Math.round(price * 100)} />}

      <SubPanel title="ATTENDEE LIMIT" sub="Set a maximum number of guests who can be accepted. Leave empty or 0 for unlimited." gap="12" testId="attendee-limit">
        <Field label="MAX ATTENDEES" htmlFor="ev-max" {...fieldTag('maxAttendees')}>
          <TextInput id="ev-max" type="number" min="0" step="1" white placeholder="Unlimited" value={maxAttendees} onChange={(e) => { const val = e.target.value; if (val === '' || parseInt(val, 10) >= 0) setMaxAttendees(val); }} onBlur={triggerSave} disabled={disabled} style={{ maxWidth: 280 }} data-testid="max-attendees-input" />
        </Field>
      </SubPanel>

      {!free && !ext && formData.fundingType === 'guaranteed' && (
        <Note testId="no-flake-note" small><span><b style={{ letterSpacing: '.08em' }}>NO FLAKE FEE ON A GUARANTEED EVENT.</b> Guests are charged the moment they check out, and a paid ticket can’t be withdrawn — so there’s nothing to penalise. Refunds are yours to issue from the event’s guest list.</span></Note>
      )}

      {!free && !ext && starter && (
        <SubPanel title={`FLAKE FEE${lock ? ' · LOCKED' : ''}`} sub="Penalty charged when a guest withdraws their application. The fee is deducted from their commitment and awarded to you." testId="flake-panel">
          <SegPills white value={flakeFeeMode} disabled={disabled || lock} ariaLabel="Flake fee mode" onChange={(k) => { setFlakeFeeMode(k); setTimeout(triggerSave, 0); }}
            options={[{ key: 'percentage', label: 'PERCENTAGE' }, { key: 'fixed', label: 'FIXED AMOUNT' }, { key: 'none', label: 'NONE' }]} />
          {flakeFeeMode === 'percentage' && (
            <Field label="PERCENTAGE" small htmlFor="ev-flake-pct" hint={flakeEst || undefined}>
              <AffixInput id="ev-flake-pct" suffix="%" type="number" min="0" max="100" white size="sm" value={flakeFeePercent} onChange={(e) => setFlakeFeePercent(e.target.value)} onBlur={triggerSave} disabled={disabled || lock} style={{ width: 140 }} />
            </Field>
          )}
          {flakeFeeMode === 'fixed' && (
            <Field label="FIXED AMOUNT" small htmlFor="ev-flake-fixed">
              <AffixInput id="ev-flake-fixed" prefix="$" type="number" min="0" step="0.01" white size="sm" placeholder="0.00" value={flakeFeeFixed} onChange={(e) => setFlakeFeeFixed(e.target.value)} onBlur={triggerSave} disabled={disabled || lock} style={{ width: 160 }} />
            </Field>
          )}
          {flakeFeeMode === 'none' && <Help body>No flake fee — guests receive a full refund when they withdraw.</Help>}
          {lock && <Help amber>FLAKE FEE SETTINGS ARE LOCKED AFTER PUBLISHING</Help>}
        </SubPanel>
      )}
    </Card>
  );

  const extrasCard = (
    <Card gap="24" rise testId="step-extras" {...stepCard(4)}>
      <SectionHead title="THE EXTRAS" sub="House rules, links, give & take, co-hosts. All optional." tag="OPTIONAL" />

      <Field as="div" label={`HOUSE RULES${ruleCount ? ` (${ruleCount})` : ''}`} hintAbove="Guests must agree to all rules before applying." testId="house-rules">
        {houseRules.map((rule, index) => (
          <span key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', background: '#F2F2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{index + 1}</span>
            <TextInput size="sm" value={rule} maxLength={500} placeholder="Enter rule…" aria-label={`Rule ${index + 1}`} disabled={disabled}
              onChange={(e) => { const updated = [...houseRules]; updated[index] = e.target.value; setHouseRules(updated); }} onBlur={triggerSave} />
            <RemoveX large label="Remove rule" onClick={() => setHouseRules((prev) => prev.filter((_, i) => i !== index))} disabled={disabled} />
          </span>
        ))}
        <Btn variant="secondary" size="sm" self="start" onClick={() => setHouseRules((prev) => [...prev, ''])} disabled={disabled}>+ ADD RULE</Btn>
      </Field>

      <ToggleRow label="GIVE / TAKE BOARD" on={!!formData.giveTakeEnabled} onToggle={() => setAndSave({ giveTakeEnabled: !formData.giveTakeEnabled })} disabled={disabled} testId="toggle-givetake"
        desc={formData.giveTakeEnabled ? 'On — guests post offers and asks to each other on the event page.' : 'Off — guests will not see the offers and asks board.'} />

      {draftId ? (
        <PartyQuestionsSection eventId={draftId} manage variant="portal" />
      ) : (
        <SubPanel title="EVENT QUESTIONS" sub="Ask the room a poll or a free-text prompt. Start filling out your event (it auto-saves a draft), then add questions here." />
      )}

      <Field as="div" label="LINKS" hintAbove="Pinned at the top of the event page. Menus, tickets, maps, anything you want one-tap reachable." testId="links">
        <LinksEditor theme="pill" value={links} onChange={(next) => { setLinks(next); triggerSave?.(); }} />
      </Field>

      {draftId ? (
        <CoHostsPanel eventId={draftId} isPrimaryHost />
      ) : (
        <SubPanel title="CO-HOSTS" sub="Start filling out your event (it auto-saves a draft), then add co-hosts here." />
      )}

      <LinkPagesPanel eventId={draftId} event={loadedEvent} clubIdFromQuery={isEditMode ? null : clubIdFromQuery} onChange={setListedOn} />
    </Card>
  );

  const simpleCard = (n) => ({ hidden: simpleStep !== n, style: { animationName: stepTick % 2 ? 'hevRiseB' : 'hevRise' } });
  const simpleEventCard = simple && (
    <Card gap="24" rise testId="simple-event" {...simpleCard(1)}>
      <SectionHead title="THE EVENT" sub="What it is, when, and where." tag="REQUIRED" />
      <Field label="EVENT NAME" required error={errors.title} htmlFor="sev-title">
        <TextInput id="sev-title" name="title" size="lg" placeholder="Enter event name" value={formData.title} onChange={handleChange} onBlur={triggerSave} error={!!errors.title} disabled={disabled} aria-label="Event Name" />
      </Field>
      <div className="hev-grid2">
        <Field label="STARTS" required error={errors.dateTime} htmlFor="sev-date">
          <TextInput id="sev-date" type="datetime-local" name="dateTime" value={formData.dateTime} onChange={handleChange} onBlur={triggerSave} error={!!errors.dateTime} disabled={disabled} />
        </Field>
        <Field label="ENDS" optional error={errors.endDateTime} htmlFor="sev-end">
          <TextInput id="sev-end" type="datetime-local" name="endDateTime" value={formData.endDateTime} min={formData.dateTime || undefined} onChange={handleChange} onBlur={triggerSave} error={!!errors.endDateTime} disabled={disabled} />
        </Field>
      </div>
      <Field as="div" label="LOCATION" required error={errors.location} id="sfield-location">
        <AddressAutocomplete
          label={null}
          value={formData.location}
          onSelect={({ address, latitude, longitude, city }) => {
            setFormData((prev) => ({ ...prev, location: address, locationLat: latitude, locationLng: longitude, locationCity: city }));
            setErrors((prev) => ({ ...prev, location: null }));
            setTimeout(triggerSave, 0);
          }}
          onChange={handleChange}
          onBlur={triggerSave}
          error={errors.location}
          disabled={disabled}
        />
        <Help>Guests see the exact address once they have a ticket.</Help>
      </Field>
      <Field as="div" label="CATEGORY" required error={errors.categories} id="sfield-categories">
        <CategoryMultiSelect
          variant="chips"
          value={formData.categories}
          error={errors.categories}
          onChange={(slugs) => {
            setFormData((prev) => ({ ...prev, categories: slugs, category: slugs[0] || '' }));
            if (slugs.length > 0) setErrors((prev) => ({ ...prev, categories: null }));
            setTimeout(triggerSave, 0);
          }}
        />
        <Help>Pick one. Add up to three in Advanced.</Help>
      </Field>
      <Field as="div" label="DESCRIPTION" required error={errors.description} id="sfield-description">
        <RichTextEditor
          bare
          placeholder="What’s the night? A few lines is plenty."
          value={formData.description}
          onChange={(html) => {
            setFormData((prev) => ({ ...prev, description: html }));
            if (errors.description) setErrors((prev) => ({ ...prev, description: null }));
          }}
          onBlur={triggerSave}
          error={errors.description}
          disabled={disabled}
        />
      </Field>
    </Card>
  );

  const simpleReviewRow = (k, v, n) => (
    <div className="hev-review-row" key={k}>
      <span className="hev-review-k">{k}</span>
      <span className="hev-review-v" data-empty={v ? undefined : ''}>{v || '—'}</span>
      <Btn variant="secondary" size="xs" onClick={() => goSimple(n)} style={{ justifySelf: 'end' }} aria-label={`Edit ${k.toLowerCase()}`}>EDIT</Btn>
    </div>
  );
  const simpleTicketsCard = simple && (
    <Card gap="24" rise testId="simple-tickets" {...simpleCard(2)}>
      <SectionHead title="TICKETS" sub="Set the price, then publish." tag={moneyWord} tagTone="black" />
      {carry.length === 0 ? (
        <Note tag="GUARANTEED" testId="simple-guaranteed-note">The night runs no matter what. Guests pay at checkout and the spot is theirs. Open guestlist, listed publicly.</Note>
      ) : (
        <Note tag="FROM ADVANCED" tone="warning" testId="simple-carry-note">
          <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span>{carry.join(' · ')} still apply.</span>
            <Btn variant="dark" size="sm" onClick={() => { setView('advanced'); goStep(3); }} testId="simple-edit-advanced">EDIT IN ADVANCED</Btn>
          </span>
        </Note>
      )}
      {!ext && <ToggleRow label="FREE EVENT" desc="No ticket price. Guests RSVP to grab a spot." on={isFreeEvent} onToggle={toggleFree} disabled={disabled} testId="simple-toggle-free" />}
      {!free && !ext && (
        <Field label="TICKET PRICE" required error={errors.ticketPrice} htmlFor="sev-price" id="sfield-ticketPrice" hint="You set your price. You get your price. We add $1 on top for your guests. Funds available 24 hours after your event.">
          <AffixInput id="sev-price" prefix="$" type="number" min="0" step="0.01" placeholder="0.00" value={formData.ticketPrice} onChange={(e) => handleCalculatorChange('ticketPrice', e.target.value)} onBlur={triggerSave} error={!!errors.ticketPrice} disabled={disabled} style={{ maxWidth: 280 }} />
        </Field>
      )}
      <Field label="MAX GUESTS" optional htmlFor="sev-max">
        <TextInput id="sev-max" type="number" min="0" step="1" placeholder="Unlimited" value={maxAttendees} onChange={(e) => { const val = e.target.value; if (val === '' || parseInt(val, 10) >= 0) setMaxAttendees(val); }} onBlur={triggerSave} disabled={disabled} style={{ maxWidth: 280 }} data-testid="simple-max-attendees" />
      </Field>
      <div className={posterShown ? 'hev-grid2' : ''} style={{ alignItems: 'start' }}>
        {posterShown && (
          <Field as="div" label="CURRENT POSTER">
            <PosterSlot posterUrl={posterShown} alt="Event poster" className="hev-poster" padClass="p-0" blur={24} fallbackKey={draftId} />
          </Field>
        )}
        <Field as="div" label={posterShown ? 'REPLACE POSTER' : 'EVENT POSTER'} optional error={errors.poster} style={{ maxWidth: 420 }}>
          <PosterDrop onFile={handlePosterAutoUpload} onError={(m) => setErrors((prev) => ({ ...prev, poster: m }))} disabled={disabled} />
        </Field>
      </div>
      <div className="hev-review" data-testid="simple-review">
        {simpleReviewRow('NAME', formData.title, 1)}
        {simpleReviewRow('WHEN', formData.dateTime ? `${fmtWhen(formData.dateTime)}${formData.endDateTime ? ` → ${fmtWhen(formData.endDateTime)}` : ''}` : '', 1)}
        {simpleReviewRow('WHERE', formData.location, 1)}
        {simpleReviewRow('MONEY', moneyStatus, 2)}
      </div>
    </Card>
  );

  const simpleFooter = simple && (
    <div className="hev-foot" data-testid="simple-footer">
      <Btn variant="dark" onClick={() => goSimple(1)} disabled={simpleStep === 1}>← BACK</Btn>
      <span className="hev-foot-sp" />
      {simpleStep === 1 && <Btn variant="white" onClick={simpleContinue} testId="simple-continue">CONTINUE →</Btn>}
      {simpleStep === 2 && (
        <>
          <Btn variant="dark" data-wide="" onClick={() => handleSubmit(false)} disabled={isSubmitting} testId="simple-save-draft">{isSubmitting ? 'SAVING…' : 'SAVE DRAFT'}</Btn>
          <Btn variant="ring-cyan" onClick={() => handleSubmit(true)} disabled={isSubmitting} testId="simple-publish">{isSubmitting ? 'PUBLISHING…' : 'PUBLISH'}</Btn>
        </>
      )}
    </div>
  );

  const reviewRow = (k, v, n, testId) => (
    <div className="hev-review-row" key={k} data-testid={testId}>
      <span className="hev-review-k">{k}</span>
      <span className="hev-review-v" data-empty={v ? undefined : ''}>{v || '—'}</span>
      <Btn variant="secondary" size="xs" onClick={() => goStep(n)} style={{ justifySelf: 'end' }} aria-label={`Edit ${k.toLowerCase()}`}>EDIT</Btn>
    </div>
  );

  // What the review previews draw from: the wizard's current values (handoff §3 — the previews
  // always show what the host is typing) plus the money model the ticketing step settled on.
  const reviewForm = {
    title: formData.title,
    description: formData.description,
    dateTime: formData.dateTime,
    endDateTime: formData.endDateTime,
    fundingDeadline: formData.fundingDeadline,
    location: formData.location,
    locationCity: formData.locationCity,
    venueName: formData.venueName,
    venueAddress: formData.venueAddress,
    categories: formData.categories,
    customCategory: formData.customCategory,
    mode: formData.mode,
    fundingType: formData.fundingType,
    pricingMode: formData.pricingMode,
    ticketPrice: formData.ticketPrice,
    fundingGoal: formData.fundingGoal,
    isFree: isFreeEvent,
    isExternal: !!formData.isExternalTicketing,
    externalTicketUrl: formData.externalTicketUrl,
    externalTicketDisplayPrice: formData.externalTicketDisplayPrice,
    giveTakeEnabled: formData.giveTakeEnabled,
    maxAttendees,
  };

  const reviewCard = isWizard && (
    <div hidden={step !== 5} className="hev-col">
      <CoverReview eventId={draftId} form={reviewForm} hidden={step !== 5} onAddPhotos={() => goStep(2)} onMediaChanged={() => setMediaVersion((v) => v + 1)} />
      <Card gap="18" rise testId="step-review" {...stepCard(5)}>
        <SectionHead title="REVIEW" sub="One last look, then publish." />
        <div className="hev-review">
          {reviewRow('NAME', formData.title, 1)}
          {reviewRow('WHEN', formData.dateTime ? `${fmtWhen(formData.dateTime)}${formData.endDateTime ? ` → ${fmtWhen(formData.endDateTime)}` : ''}` : '', 1)}
          {reviewRow('WHERE', formData.venueName ? `${formData.venueName} · ${formData.location}` : formData.location, 2)}
          {reviewRow('GUESTLIST', `${formData.mode === 'curated' ? 'Curated' : 'Open'}${formData.isPrivate ? ' · Private' : ''}`, 1)}
          {reviewRow('MONEY', moneyStatus, 3)}
          {reviewRow('EXTRAS', extrasBits.join(' · ') || 'None', 4)}
          {reviewRow('LISTED ON', listedNames.join(' · ') || 'Nobody yet', 4, 'review-listed-on')}
        </div>
      </Card>
      {isEditMode && paidCount > 0 && (
        <Note tag="HEADS UP" tone="warning" testId="edit-warning">
          {paidCount} {paidCount === 1 ? 'person' : 'people'} already bought. Changing the date, the price or the address messages every one of them.
        </Note>
      )}
    </div>
  );

  const validation = submitError && (
    <Note tone="danger" title="VALIDATION REQUIRED" testId="submit-error">{submitError}</Note>
  );

  // The from-chat review sheet (plan detection): PUBLISH is blocked until the validator passes and
  // reads FILL IN n REQUIRED with the missing fields under it.
  const sheetMissing = sheet ? missingLabels(validateEventForm(formData, { isFreeEvent, numberOfGuests, rsvpEligible, requireCoords: true })) : [];
  const sheetFooter = sheet && (
    <div className="hev-sheet-foot" data-testid="sheet-footer" style={{ position: 'sticky', bottom: 0, display: 'flex', flexDirection: 'column', gap: 6, background: '#fff', borderTop: '2px solid #F2F2F2', padding: compact ? '10px 0 14px' : '14px 0 20px', marginTop: 4 }}>
      <Btn size={compact ? 'md' : 'lg'} full onClick={() => handleSubmit(true)} disabled={isSubmitting || sheetMissing.length > 0} testId="sheet-publish" style={{ opacity: sheetMissing.length ? 0.5 : 1 }}>
        {isSubmitting ? 'PUBLISHING…' : sheetMissing.length ? `FILL IN ${sheetMissing.length} REQUIRED` : 'PUBLISH EVENT'}
      </Btn>
      <span className="hev-mono-caps" style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, letterSpacing: '.1em', color: '#6B7280' }} data-testid="sheet-publish-note">
        {sheetMissing.length ? `MISSING: ${sheetMissing.map((l) => l.toUpperCase()).join(', ')}` : [formData.isPrivate ? 'PRIVATE' : 'PUBLIC', formData.mode === 'curated' ? 'CURATED' : 'OPEN', ext ? 'EXTERNAL TICKETS' : free ? 'FREE' : 'PAID', prefill?.threadName ? `CARD POSTS TO ${prefill.threadName.toUpperCase()}` : null].filter(Boolean).join(' · ')}
      </span>
    </div>
  );

  const footer = sheet ? sheetFooter : !isWizard ? (
    // Embedded (the gathering wizard): one form, the old two-button row.
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 8 }} data-testid="embedded-footer">
      {isEditMode
        ? <Btn size="lg" onClick={() => handleSubmit(false)} disabled={isSubmitting}>{isSubmitting ? 'SAVING…' : 'SAVE CHANGES'}</Btn>
        : (
          <>
            <Btn variant="secondary" size="lg" onClick={() => handleSubmit(false)} disabled={isSubmitting}>{isSubmitting ? 'SAVING…' : 'SAVE DRAFT'}</Btn>
            <Btn size="lg" onClick={() => handleSubmit(true)} disabled={isSubmitting}>{isSubmitting ? 'SAVING…' : 'PUBLISH'}</Btn>
          </>
        )}
      <Btn variant="text" size="lg" onClick={() => onCancel?.()} disabled={isSubmitting || isDeleting} style={{ color: '#6B7280' }}>CANCEL</Btn>
    </div>
  ) : (
    <div className="hev-foot" data-testid="edit-footer">
      <Btn variant="dark" onClick={() => goStep(step - 1)} disabled={step === 1}>← BACK</Btn>
      {!createMode ? (
        <>
          <Btn variant="dark" onClick={() => goStep(step + 1)} disabled={step === 5}>NEXT →</Btn>
          <span className="hev-foot-sp" />
          {(onViewPublic || loadedEvent?.inviteCode) && (
            <Btn variant="link" className="hev-foot-public" onClick={() => (onViewPublic ? onViewPublic() : navigate(`/events/${loadedEvent.inviteCode}`))}>VIEW PUBLIC PAGE ↗</Btn>
          )}
          <Btn variant="ring-white" onClick={() => handleSubmit(false)} disabled={isSubmitting} testId="edit-save">{saving ? 'SAVING…' : 'SAVE CHANGES'}</Btn>
        </>
      ) : (
        <>
          <span className="hev-foot-sp" />
          {basicsComplete && step >= 2 && step < 5 && <Btn variant="text" onClick={() => goStep(5)}>SKIP TO PUBLISH →</Btn>}
          {(step === 3 || step === 4) && <Btn variant="dark" onClick={() => goStep(step + 1)}>SKIP</Btn>}
          {step < 5 && <Btn variant="white" onClick={wizardContinue} testId="wizard-continue">CONTINUE →</Btn>}
          {step === 5 && (
            <>
              <Btn variant="dark" data-wide="" onClick={() => handleSubmit(false)} disabled={isSubmitting} testId="wizard-save-draft">{isSubmitting ? 'SAVING…' : 'SAVE DRAFT'}</Btn>
              <Btn variant="ring-cyan" onClick={() => handleSubmit(true)} disabled={isSubmitting} testId="wizard-publish">{isSubmitting ? 'PUBLISHING…' : 'PUBLISH'}</Btn>
            </>
          )}
        </>
      )}
    </div>
  );

  const belowFooter = standalone && (
    <>
      {createMode && <button type="button" className="hev-cancel" onClick={() => navigate('/host')} disabled={isSubmitting || isDeleting}>CANCEL</button>}
      {isEditMode && eventStatus === 'draft' && (
        <button type="button" className="hev-cancel" style={{ color: '#B91C1C' }} onClick={handleDelete} disabled={isSubmitting || isDeleting}>{isDeleting ? 'DELETING…' : 'DELETE EVENT'}</button>
      )}
    </>
  );

  // Embedded: the money and extras fold away (one form, no stepper).
  const fold = (title, sub, statusWord, open, onToggle, body, testId) => (
    <Card gap="14" testId={testId}>
      <button type="button" onClick={onToggle} className="hev-sec" style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', width: '100%' }} aria-expanded={open}>
        <span className="hev-sec-text"><span className="hev-sec-title">{title}</span><span className="hev-sec-sub">{sub}</span></span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span className="hev-tag">{statusWord}</span><span style={{ fontSize: 18, lineHeight: 1 }}>{open ? '−' : '+'}</span></span>
      </button>
      {open && body}
    </Card>
  );

  const body = isWizard ? (simple ? (
    // SIMPLE: two cards, same state, same validator, same submit.
    <>
      {viewSwitch}
      {wizardTop}
      {simplePills}
      {simpleEventCard}
      {simpleTicketsCard}
      {afterSteps}
      {validation}
      {simpleFooter}
      {belowFooter}
      <Toast text={toast} />
    </>
  ) : (
    <>
      {viewSwitch}
      {wizardTop}
      {stepPills}
      {basicsCard}
      {placeCard}
      {ticketingCard}
      {extrasCard}
      {reviewCard}
      {afterSteps}
      {validation}
      {footer}
      {belowFooter}
      <Toast text={toast} />
    </>
  )) : sheet ? (
    // The from-chat review: every section stacked, nothing folded, the sheet's own footer.
    <>
      {basicsCard}
      {placeCard}
      {ticketingCard}
      {extrasCard}
      {validation}
      {footer}
    </>
  ) : (
    <>
      <div className="hev-wiz-top"><span className="hev-wiz-kick">{isEditMode ? 'EDIT EVENT' : 'NEW EVENT'}</span></div>
      <Help>Fill in the basics, then choose how tickets work. Optional extras are below.</Help>
      {basicsCard}
      {placeCard}
      {fold('THE MONEY', 'How the night funds.', moneyWord, moneyOpen, () => setMoneyOpen((o) => !o), ticketingCard, 'fold-money')}
      {fold('THE EXTRAS', 'House rules, links, give & take, co-hosts. All optional.', 'OPTIONAL', extrasOpen, () => setExtrasOpen((o) => !o), extrasCard, 'fold-extras')}
      {validation}
      {footer}
    </>
  );

  if (!standalone) {
    return (
      <form onSubmit={(e) => e.preventDefault()} className={`hev-col hev${inline ? '' : ' hev-embedded'}`} data-sheet={sheet ? '' : undefined} data-compact={compact ? '' : undefined} style={{ background: 'transparent', minHeight: 0, ...(sheet ? { flex: 1, overflowY: 'auto', padding: compact ? '2px 16px 0' : '4px 24px 0' } : {}) }} data-testid={sheet ? 'event-builder-sheet' : inline ? 'event-builder-inline' : 'event-builder-embedded'}>
        {body}
      </form>
    );
  }

  return (
    <SurfacePage bare showTopBarOnMobile>
      <div className="hev" style={{ '--hev-top': `${topH}px` }} data-testid="event-builder-page">
        <HostEventHeader
          create={createMode}
          title={formData.title}
          status={isEditMode ? eventStatus : 'draft'}
          dateTime={formData.dateTime}
          step={simple ? simpleStep : step}
          stepTotal={simple ? 2 : 5}
          viewWord={simple ? 'SIMPLE' : ''}
          saveState={saveState}
          backLabel="MY EVENTS"
          onBack={() => navigate('/host?tab=events')}
        />
        <main className="hev-main" data-create={createMode ? '' : undefined}>
          <form onSubmit={(e) => e.preventDefault()} className="hev-col">
            {body}
          </form>
        </main>
      </div>
    </SurfacePage>
  );
}
